import "./list-transaction.scss";

import { useUserIdentityContext } from "@core/services/user-identity.context";
import TagColor from "@core/ui/tag-color/tag-color";
import { GetTransactionModel } from "@factory/model/query-model-transaction";
import {
  useQyGetTransaction,
  useQyGetTransactionCSV,
} from "@factory/query/transaction.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { currencyFormat } from "@shared/utility/formats/currency-format";
import { numberFormat } from "@shared/utility/formats/number-format";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { Tag } from "primereact/tag";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate } from "react-router-dom";

import { appFeature } from "../../../app.feature";
import { FilterTransaction } from "../filter-transaction/filter-transaction";
import { useFilterTransactionContext } from "../filter-transaction/filter-transaction.context";

export interface ListTransactionProps { }
export function ListTransaction() {
  // THIS IS THE LOCAL DECLARATION
  const { pointsText, stampsText } = appFeature.config.label;
  const excludedColumns = appFeature.features.transaction["list-page"]
    .excludedColumns as string[];
  const { formatDateTimeLocal } = useDateTimeContext();
  const { getClientFeatures } = useUserIdentityContext();
  const navigate = useNavigate();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const { filterChipsElement, getFilterEntityValues } =
    useFilterTransactionContext();
  const rawFilterValues = getFilterEntityValues() as Record<string, string>;
  const selectedDate = rawFilterValues?.date || undefined;
  const { date: _dateFilter, ...filterValues } = rawFilterValues || {};
  const [exportStartDate, setExportStartDate] = useState(selectedDate || "");
  const [exportEndDate, setExportEndDate] = useState(selectedDate || "");
  const hasInvalidExportDateRange =
    !!exportStartDate &&
    !!exportEndDate &&
    exportStartDate.localeCompare(exportEndDate) > 0;
  const clientFeatures = getClientFeatures();

  // THIS IS THE QUERY DECLARATION
  const {
    data: transactionResponse,
    isLoading,
    isError,
    error,
  } = useQyGetTransaction(
    searchTerm,
    rowLimit,
    pageNumber,
    sort, // TODO
    filterValues,
  );
  const {
    refetch: refetchTransactionCsv,
    isFetching: isExportingCsv,
  } = useQyGetTransactionCSV(
    searchTerm,
    rowLimit,
    pageNumber,
    undefined,
    exportStartDate || undefined,
    exportEndDate || undefined,
    sort,
    filterValues,
    false
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetTransactionModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleExportCsv = async () => {
    const result = await refetchTransactionCsv();
    const csvContent = result.data?.csv || "";
    if (!csvContent) {
      return;
    }

    const file = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "transactions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportDialog(false);
  };
  const handleOpenExportDialog = () => {
    setExportStartDate(selectedDate || "");
    setExportEndDate(selectedDate || "");
    setExportDialog(true);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterTransaction />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{transactionResponse?.count}</b>
      </h5>

      <DataTable
        value={transactionResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="branch_name" header="Branch" sortable></Column>
        {clientFeatures?.featureReceiptNotes ? (
          <Column field="receipt_number" header="Receipt" sortable></Column>
        ): null}
        <Column field="reward_name" header="Reward" sortable></Column>
        <Column
          field="user_code"
          body={(cell) => (
            <span>
              {cell.first_name} {cell.last_name}
            </span>
          )}
          header="Customer"
          sortable
        ></Column>
        <Column field="campaign_name" header="Campaign" sortable></Column>
        <Column
          field="action"
          body={(cell) => <TagColor value={cell.action} />}
          header="Action"
          sortable
        ></Column>
        {!excludedColumns.includes("amount") ? (
          <Column
            field="amount"
            body={(cell) => currencyFormat(cell.amount)}
            header="Amount"
            sortable
          ></Column>
        ) : null}
        <Column
          field="points"
          body={(cell) => numberFormat(cell.points)}
          header={pointsText}
          sortable
        ></Column>
        <Column field="stamp" header={stampsText} sortable></Column>
        <Column
          field="updated_last_name"
          header="Updated"
          body={(cell) => (
            <div>
              {cell.updated_first_name} {cell.updated_last_name}
            </div>
          )}
          sortable
        ></Column>
        <Column
          body={(cell) => formatDateTimeLocal(cell.updated_at)}
          field="updated_at"
          header="Date"
          sortable
        ></Column>
        <Column
          field="remarks"
          header="Remarks"
          body={(item) => (
            <div>
              {item.voided_by ? (
                <Tag
                  className="bg-red-500"
                >
                  <div className="flex align-items-center gap-2">
                    VOID
                  </div>
                </Tag>
              ) : (
                item.remarks
              )}
            </div>
          )}
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={transactionResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );
  const exportDialogFooter = (
    <div className="flex gap-2 justify-end">
      <Button
        label="Close"
        severity="secondary"
        outlined
        onClick={() => setExportDialog(false)}
      />
      <Button
        label="Export CSV"
        onClick={handleExportCsv}
        loading={isExportingCsv}
        disabled={hasInvalidExportDateRange}
      />
    </div>
  );
  const exportDialogElement = (
    <Dialog
      header="Export Transactions CSV"
      visible={exportDialog}
      style={{ width: "30rem" }}
      onHide={() => setExportDialog(false)}
      footer={exportDialogFooter}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="export-start-date">Start Date</label>
          <InputText
            id="export-start-date"
            type="date"
            value={exportStartDate}
            onChange={(e) => setExportStartDate(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="export-end-date">End Date</label>
          <InputText
            id="export-end-date"
            type="date"
            value={exportEndDate}
            onChange={(e) => setExportEndDate(e.target.value)}
          />
        </div>
        {hasInvalidExportDateRange ? (
          <small className="text-red-500">
            End date must be the same as or after start date.
          </small>
        ) : null}
      </div>
    </Dialog>
  );

  return (
    <div id="ListTransaction" className="transaction">
      <HeaderContent title={<h1>Transaction</h1>}>
        <span className="flex gap-2">
          <Button
            label="Export CSV"
            onClick={handleOpenExportDialog}
            outlined
            severity="secondary"
          />
        </span>
      </HeaderContent>

      <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
      {exportDialogElement}
    </div>
  );
}

export default ListTransaction;
